/** Foreground feedback only. Its independent DOM never remounts the chat or shifts history. */
export class LoadingStatus {
  private readonly entries = new Set<{ label: string; started: number; reveal: number; valid: () => boolean }>();
  private timer: number | undefined;
  private readonly node = document.createElement('div');
  private readonly label = document.createElement('span');
  private readonly detail = document.createElement('span');

  constructor() {
    this.node.className = 'request-status';
    this.node.hidden = true;
    this.node.setAttribute('role', 'status');
    this.node.setAttribute('aria-live', 'polite');
    this.node.setAttribute('aria-atomic', 'true');
    const spinner = document.createElement('span');
    spinner.className = 'spinner'; spinner.setAttribute('aria-hidden', 'true');
    const text = document.createElement('span');
    this.label.className = 'request-status-label'; this.detail.className = 'request-status-detail';
    text.append(this.label, this.detail); this.node.append(spinner, text);
    document.body.append(this.node);
    window.addEventListener('online', () => this.refresh());
    window.addEventListener('offline', () => this.refresh());
  }

  begin(label: string, valid: () => boolean, delay = 350): { update: (label: string) => void; finish: () => void } {
    const started = performance.now();
    const entry = { label, valid, started, reveal: started + delay };
    this.entries.add(entry); this.refresh();
    return {
      update: label => { entry.label = label; this.refresh(); },
      finish: () => { this.entries.delete(entry); this.refresh(); },
    };
  }

  async run<T>(label: string, valid: () => boolean, action: () => Promise<T>): Promise<T> {
    const pending = this.begin(label, valid);
    try { return await action(); } finally { pending.finish(); }
  }

  refresh(): void {
    window.clearTimeout(this.timer); this.timer = undefined;
    const now = performance.now();
    for (const entry of this.entries) if (!entry.valid()) this.entries.delete(entry);
    // Most recent eligible action wins; finishing it reveals any older unfinished work.
    const visible = [...this.entries].filter(entry => now >= entry.reveal).at(-1);
    this.node.hidden = !visible;
    const label = visible?.label ?? '';
    const detail = !visible ? '' : !navigator.onLine ? 'Нет подключения к сети.'
      : now - visible.started >= 5000 ? 'Ожидание заняло больше времени, чем обычно.' : '';
    if (this.label.textContent !== label) this.label.textContent = label;
    if (this.detail.textContent !== detail) this.detail.textContent = detail;
    this.detail.hidden = !detail;
    const next = [...this.entries].flatMap(entry => [entry.reveal, entry.started + 5000]).filter(time => time > now);
    if (next.length) this.timer = window.setTimeout(() => this.refresh(), Math.ceil(Math.min(...next) - now));
  }
}
